import { Icon } from "@iconify/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "use-intl";
import { getAppwriteClient } from "$/modules/appwrite/lib/appwrite";
import type {
	LiveAnswer,
	LiveSession,
	LiveTeam,
} from "$/modules/appwrite/types/collections";
import { useGetUser } from "$/modules/auth/hooks/useGetUser";
import { teamColor } from "$/modules/projects/lib/live-battle";
import {
	getLiveAnswers,
	getLiveSession,
	getLiveTeams,
	updateLiveSession,
} from "$/modules/projects/lib/live-sessions";
import {
	LiveBattleLeaderboard,
	LiveBattleScoreboard,
} from "$/modules/projects/ui/live-battle-scoreboard";
import { LiveRoundController } from "$/modules/projects/ui/live-round-controller";
import { cn } from "$/modules/shared/lib/utils";
import { useQuizPlayer } from "$/modules/shared/provider/quiz-player-provider";
import { Badge } from "$/modules/shared/ui/badge";
import { Button } from "$/modules/shared/ui/button";
import { Card, CardContent } from "$/modules/shared/ui/card";

export const Route = createFileRoute(
	"/$locale/quiz-player/$projectId/live/$sessionId/",
)({
	loader: ({ params }) => getLiveSession(params.sessionId),
	component: LiveQuizController,
});

const normalizedAnswers = (answers: string[]) =>
	answers.map((answer) => answer.trim().toLocaleLowerCase()).sort();

const isCorrectAnswer = (answer: LiveAnswer, correctOptions: string[]) => {
	const submitted = normalizedAnswers(answer.answers);
	const correct = normalizedAnswers(correctOptions);
	return (
		submitted.length === correct.length &&
		submitted.every((item, index) => item === correct[index])
	);
};

function LiveQuizController() {
	const session = Route.useLoaderData();
	return session.questionPlanJson ? (
		<LiveRoundController initialSession={session} />
	) : (
		<LegacyLiveQuizController />
	);
}

function LegacyLiveQuizController() {
	const t = useTranslations("live");
	const initialSession = Route.useLoaderData();
	const { locale, projectId } = Route.useParams();
	const { project } = useQuizPlayer();
	const user = useGetUser();
	const [session, setSession] = useState<LiveSession>(initialSession);
	const [teams, setTeams] = useState<LiveTeam[]>([]);
	const [answers, setAnswers] = useState<LiveAnswer[]>([]);
	const questions = useMemo(
		() => project.questionnaires?.flatMap((item) => item.questions ?? []) ?? [],
		[project],
	);
	const question = questions[session.currentQuestionIndex];
	const isHost = session.hostId === user?.$id;
	const correctAnswersVisible = session.correctAnswersVisible ?? false;

	const refreshRoundData = useCallback(async () => {
		const [teamResult, answerResult] = await Promise.all([
			getLiveTeams(session.$id),
			getLiveAnswers(session.$id),
		]);
		setTeams(teamResult.rows);
		setAnswers(answerResult.rows);
	}, [session.$id]);

	useEffect(() => {
		void refreshRoundData();
	}, [refreshRoundData]);

	useEffect(() => {
		const appwrite = getAppwriteClient();
		return appwrite.subscribe(
			`tablesdb.${session.$databaseId}.tables.liveSessions.rows.${session.$id}`,
			(event) => setSession(event.payload as LiveSession),
		);
	}, [session.$databaseId, session.$id]);

	useEffect(() => {
		const appwrite = getAppwriteClient();
		return appwrite.subscribe(
			`tablesdb.${session.$databaseId}.tables.liveAnswers.rows`,
			() => void refreshRoundData(),
		);
	}, [refreshRoundData, session.$databaseId]);

	const answersForQuestion = useMemo(
		() => answers.filter((answer) => answer.questionId === question?.$id),
		[answers, question?.$id],
	);
	const answersByTeam = useMemo(
		() => new Map(answersForQuestion.map((answer) => [answer.teamId, answer])),
		[answersForQuestion],
	);

	const start = () =>
		updateLiveSession(session, {
			status: "playing",
			currentQuestionIndex: session.currentQuestionIndex,
			correctAnswersVisible: false,
		});
	const revealCorrectAnswers = () =>
		updateLiveSession(session, {
			status: session.status,
			currentQuestionIndex: session.currentQuestionIndex,
			correctAnswersVisible: true,
		});
	const next = () =>
		updateLiveSession(
			session,
			session.currentQuestionIndex >= questions.length - 1
				? {
						status: "finished",
						currentQuestionIndex: session.currentQuestionIndex,
						correctAnswersVisible: false,
					}
				: {
						status: "playing",
						currentQuestionIndex: session.currentQuestionIndex + 1,
						correctAnswersVisible: false,
					},
		);

	if (!isHost) {
		return (
			<main className="grid min-h-screen place-items-center bg-content2 p-4">
				<Card className="w-full max-w-md border border-default-200 text-center">
					<CardContent className="gap-4 p-8">
						<Icon
							icon="lucide:lock-keyhole"
							className="mx-auto size-8 text-warning"
						/>
						<h1 className="font-display text-2xl font-bold">{t("hostOnly")}</h1>
						<p className="text-foreground-500">{t("hostOnlyDescription")}</p>
						<Button variant="muted" asChild>
							<Link
								to="/$locale/quiz-player/$projectId/questionnaires"
								params={{ locale, projectId }}
							>
								{t("backToQuiz")}
							</Link>
						</Button>
					</CardContent>
				</Card>
			</main>
		);
	}

	return (
		<main className="min-h-screen bg-content2 p-4 sm:p-8">
			<section className="mx-auto max-w-6xl space-y-5">
				<header className="flex flex-wrap items-center justify-between gap-3">
					<div>
						<p className="text-sm font-semibold text-primary">
							{t("controls")} · {project.name}
						</p>
						<h1 className="font-display text-3xl font-bold sm:text-4xl">
							{question?.question ?? t("noQuestions")}
						</h1>
					</div>
					<div className="flex items-center gap-2">
						<Badge
							variant="secondary"
							className={cn({
								"bg-primary/15 text-primary":
									(session.status === "finished" ? "default" : "primary") ===
									"primary",
							})}
						>
							{session.status === "lobby"
								? t("lobby")
								: session.status === "finished"
									? t("finished")
									: t("roundProgress", {
											current: session.currentQuestionIndex + 1,
											total: questions.length,
										})}
						</Badge>
						<Button
							aria-label={t("leaveQuiz")}
							variant="ghost"
							size="icon"
							asChild
						>
							<Link
								to="/$locale/quiz-player/$projectId/questionnaires"
								params={{ locale, projectId }}
							>
								<Icon icon="lucide:x" />
							</Link>
						</Button>
					</div>
				</header>

				{session.status === "finished" && (
					<LiveBattleScoreboard
						teams={teams}
						answers={answers}
						questions={questions}
					/>
				)}

				{session.status !== "finished" && (
					<>
						<LiveBattleLeaderboard
							teams={teams}
							answers={answers}
							questions={questions}
						/>

						<Card className="border border-default-200">
							<CardContent className="gap-5 p-5 sm:p-7">
								<div className="flex flex-wrap items-center justify-between gap-3 border-b border-default-100 pb-5">
									<div className="flex items-center gap-3">
										<div className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
											<Icon icon="lucide:users-round" className="size-5" />
										</div>
										<div>
											<p className="font-semibold">
												{t("teamsAnswered", {
													answered: answersByTeam.size,
													total: teams.length,
												})}
											</p>
											<p className="text-sm text-foreground-500">
												{t("answersAppear")}
											</p>
										</div>
									</div>
									{session.status === "lobby" && (
										<Button
											onClick={start}
											disabled={questions.length === 0}
											variant="default"
											type="button"
										>
											{<Icon icon="lucide:play" />}
											{t("startRound")}
										</Button>
									)}
									{session.status === "playing" && (
										<div className="flex flex-wrap gap-2">
											{!correctAnswersVisible && (
												<Button
													onClick={revealCorrectAnswers}
													variant="success"
													className="bg-success text-success-foreground hover:bg-success/80"
													type="button"
												>
													{<Icon icon="lucide:eye" />}
													{t("revealCorrectAnswers")}
												</Button>
											)}
											<Button onClick={next} variant="default" type="button">
												{session.currentQuestionIndex >= questions.length - 1
													? t("endRound")
													: t("nextQuestion")}
												{<Icon icon="lucide:arrow-right" />}
											</Button>
										</div>
									)}
								</div>

								{question && correctAnswersVisible && (
									<div className="rounded-2xl border border-success-200 bg-success-50 p-4">
										<p className="mb-1 text-xs font-bold uppercase tracking-wider text-success-700">
											{t("correctAnswers", {
												suffix: question.correctOption.length > 1 ? "en" : "",
											})}
										</p>
										<p className="font-semibold text-success-900">
											{question.correctOption.join(" · ") || t("noSolution")}
										</p>
									</div>
								)}

								<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
									{teams.map((team, index) => {
										const answer = answersByTeam.get(team.$id);
										const isCorrect =
											answer &&
											question &&
											isCorrectAnswer(answer, question.correctOption);
										const color = teamColor(team, index);
										return (
											<article
												key={team.$id}
												className={`rounded-2xl border border-l-4 p-4 transition-colors ${!answer || !correctAnswersVisible ? "border-default-200 bg-content2" : isCorrect ? "border-success-300 bg-success-50" : "border-danger-200 bg-danger-50"}`}
												style={{ borderLeftColor: color }}
											>
												<div className="mb-3 flex items-center justify-between gap-2">
													<p className="flex items-center gap-2 font-semibold">
														<span className="text-lg">
															{team.emoji || "🎯"}
														</span>
														{team.name}
													</p>
													<Badge
														variant="secondary"
														className={cn({
															"bg-success/15 text-success":
																(!answer
																	? "default"
																	: correctAnswersVisible && isCorrect
																		? "success"
																		: correctAnswersVisible
																			? "danger"
																			: "primary") === "success",
															"bg-danger/15 text-danger":
																(!answer
																	? "default"
																	: correctAnswersVisible && isCorrect
																		? "success"
																		: correctAnswersVisible
																			? "danger"
																			: "primary") === "danger",
															"bg-primary/15 text-primary":
																(!answer
																	? "default"
																	: correctAnswersVisible && isCorrect
																		? "success"
																		: correctAnswersVisible
																			? "danger"
																			: "primary") === "primary",
														})}
													>
														{!answer
															? t("waiting")
															: correctAnswersVisible && isCorrect
																? t("correct")
																: correctAnswersVisible
																	? t("incorrect")
																	: t("answerReceived")}
													</Badge>
												</div>
												<p className="min-h-6 text-sm leading-6 text-foreground-600">
													{answer ? answer.answers.join(" · ") : t("noAnswer")}
												</p>
											</article>
										);
									})}
								</div>
							</CardContent>
						</Card>
					</>
				)}
			</section>
		</main>
	);
}
